// Deterministic FINDINGS / RECOMMENDED SCOPE bullets for the DG Summary.
//
// Each summary page carries a "[Finding]" / "[Recommendation]" placeholder
// row with a few styled blank rows beneath. Findings are generated from the
// same survey answers the rest of the export uses, per the page's section
// (nearest preceding SECTION header). Narrative judgment stays out: every
// bullet states a surveyed fact, and its recommendation follows the Formula
// Reference Guide's rule for that fact. Sections with no derivable findings
// keep their placeholder untouched.

import type { CellWrite } from "./xlsxTemplatePatcher.ts";
import { findSheetName, readSheetCells } from "./xlsxTemplatePatcher.ts";
import { SurveyAnswers } from "./gcEvaluationFill.ts";
import { SUMMARY_SHEET_RE } from "./exportCore.ts";

const SUMMARY_SHEET = "DG Leasing - Summary";
const MAX_ROWS = 4;

interface FindingPair {
  finding: string;
  recommendation: string;
}

const fmt = (n: number) => n.toLocaleString("en-US");

function hvacFindings(a: SurveyAnswers): FindingPair[] {
  const pairs: FindingPair[] = [];

  const units: Array<{ tons: number | null; age: number | null; action: string | null }> = [];
  for (let i = 1; i <= 6; i++) {
    const tons = a.num(`Unit ${i} — Tonnage`);
    const age = a.num(`Unit ${i} — Age (years)`) ?? a.num(`Unit ${i} — Age`);
    const action = a.text(`Unit ${i} — Action`);
    if (tons !== null || action !== null) units.push({ tons, age, action });
  }
  if (units.length > 0) {
    const aged = units.filter((u) => u.age !== null && u.age >= 10).length;
    const replace = units.filter((u) => /replace/i.test(u.action ?? "")).length;
    const tuneUp = units.filter((u) => /tune/i.test(u.action ?? "")).length;
    pairs.push({
      finding: `${units.length} rooftop unit${units.length === 1 ? "" : "s"} surveyed${aged > 0 ? `; ${aged} at or beyond 10 yrs` : ""}`,
      recommendation: [
        replace > 0 ? `Replace ${replace}` : null,
        tuneUp > 0 ? `Tune-up ${tuneUp}` : null,
      ]
        .filter(Boolean)
        .join(", ")
        ? `${[replace > 0 ? `Replace ${replace}` : null, tuneUp > 0 ? `Tune-up ${tuneUp}` : null].filter(Boolean).join(", ")} per Survey Form actions`
        : "Action per Survey Form",
    });
  }

  const deficit = a.num("Tonnage deficit / add");
  const zone = a.text("Climate Zone") ?? a.text("Zone");
  const required = a.num(
    "Required tonnage per zone (Building SF ÷ Zone SF/Ton, round up to 0.5t)"
  );
  const existing = a.num("Existing total tonnage (sum of all units)");
  if (deficit !== null && deficit > 0) {
    pairs.push({
      finding: `Existing ${existing ?? "?"}T vs Zone ${zone ?? "?"} minimum ${required ?? "?"}T`,
      recommendation: `Add ${deficit}T to meet zone minimum (${deficit}T × $3,500/ton)`,
    });
  }

  const notWorking = a.yes("Any HVAC units not working correctly (per manager)?");
  const comments = a.text("Additional HVAC comments");
  if (notWorking === true) {
    pairs.push({
      finding: `Manager reports unit issues${comments && !/^n\/?a$|^none$/i.test(comments) ? `: "${comments.slice(0, 70)}"` : ""}`,
      recommendation: "GC to assess during tune-up scope",
    });
  }

  return pairs;
}

function parkingFindings(a: SurveyAnswers): FindingPair[] {
  const pairs: FindingPair[] = [];
  const lot = a.num("Parking Lot SF") ?? a.num("Lot SF");
  const lotText = lot !== null ? `${fmt(lot)} sf` : "lot";

  const damaged = a.num("% of parking lot with damaged pavement");
  const depth = a.num("Average depth of damaged areas / holes (inches)");
  if (damaged !== null && damaged > 0) {
    pairs.push({
      finding: `${damaged}% of pavement damaged${depth !== null ? ` at ${depth}" depth` : ""}`,
      recommendation: `Crack fill + seal coat (${lotText} × $0.625/sf)`,
    });
  }

  const stripe = a.num("Which photo best represents existing parking lot striping quality?");
  if (stripe !== null) {
    pairs.push({
      finding: `Striping quality rating: ${stripe}`,
      recommendation: `Full restripe (${lotText} × $0.20/sf)`,
    });
  }

  const rating = a.num("Which photo best matches existing parking lot condition?");
  if (rating !== null) {
    pairs.push({
      finding: `Overall lot condition rating: ${rating}`,
      recommendation:
        rating <= 1
          ? "Best-scope resurfacing per rating 0–1"
          : rating <= 3
            ? "Better-scope resurfacing per rating 2–3"
            : "Spot repair — full resurfacing not indicated at rating 4+",
    });
  }

  const stops = a.num("How many parking stops are missing?");
  if (stops !== null && stops > 0) {
    pairs.push({
      finding: `${stops} parking stop${stops === 1 ? "" : "s"} missing`,
      recommendation: `Install ${stops} × $150/each`,
    });
  }

  const drainage = a.yes("Evidence of drainage issues?");
  if (drainage === true) {
    pairs.push({
      finding: "Drainage issues observed",
      recommendation: "Storm drain cleaning / GC to assess",
    });
  }

  return pairs;
}

/** Section title (lowercased match) → findings builder. */
const SECTION_BUILDERS: Array<{ match: RegExp; build: (a: SurveyAnswers) => FindingPair[] }> = [
  { match: /hvac/i, build: hvacFindings },
  { match: /parking/i, build: parkingFindings },
];

/** Pure core: locate [Finding]/[Recommendation] rows and emit bullet writes. */
export function buildFindingsWrites(
  cells: Map<string, string>,
  answers: SurveyAnswers,
  sheetName: string = SUMMARY_SHEET
): CellWrite[] {
  const sectionRows: Array<{ row: number; title: string }> = [];
  for (const [ref, text] of cells) {
    const m = ref.match(/^B(\d+)$/);
    if (m && /^SECTION \d+/i.test(text.trim())) {
      sectionRows.push({ row: Number(m[1]), title: (cells.get(`B${Number(m[1]) + 1}`) ?? "").trim() });
    }
  }
  sectionRows.sort((a, b) => a.row - b.row);

  const writes: CellWrite[] = [];
  for (const [ref, text] of cells) {
    const m = ref.match(/^B(\d+)$/);
    if (!m || !text.includes("[Finding]")) continue;
    const row = Number(m[1]);
    const section = [...sectionRows].reverse().find((s) => s.row < row)?.title ?? "";
    const builder = SECTION_BUILDERS.find((b) => b.match.test(section));
    if (!builder) continue;
    const pairs = builder.build(answers).slice(0, MAX_ROWS);
    if (pairs.length === 0) continue;
    pairs.forEach((pair, i) => {
      writes.push({ sheet: sheetName, cell: `B${row + i}`, value: `•   ${pair.finding}` });
      writes.push({ sheet: sheetName, cell: `H${row + i}`, value: `→   ${pair.recommendation}` });
    });
  }
  return writes;
}

/** Findings writes for a template workbook; [] when it has no Summary sheet. */
export async function collectFindingsWrites(
  templateBuf: ArrayBuffer,
  answers: SurveyAnswers
): Promise<CellWrite[]> {
  const summarySheet = (await findSheetName(templateBuf, SUMMARY_SHEET_RE)) ?? SUMMARY_SHEET;
  const cells = await readSheetCells(templateBuf, summarySheet);
  if (cells.size === 0) return [];
  return buildFindingsWrites(cells, answers, summarySheet);
}
